// React & dayjs
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { SyntheticEvent, useEffect, useMemo, useState } from 'react'

// Redux
import { AppDispatch } from '../../../../store/store'
import { useDispatch, useSelector } from 'react-redux'
import { TimesheetStoreState } from '../../../../store/timesheetStore'

// Store slices & actions
import {
	ApprovalRequest,
	WorktimesRequest,
	acceptApproval,
} from '../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { hasChanges } from '../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalHasChangesSlice'
import {
	Services,
	WorkTime,
	handleDetails,
	handleTimesheetApprovalDetails,
	handleTimesheetInitialStatusDetails,
	handleTimesheetWorkTimesDetails,
} from '../../../../store/slices/timesheet/timesheetPageSlice'

// Utils & hooks
import { devWarn } from '../../../../utils/feedback'
import { WorkTimePeriod } from './WorkTimes/WorkPeriod'
import { useIsTimesheetApproved } from '../../../../utils/hooks'
import { getWorkTimePeriods } from './TimesheetServicesHelpers'

// Custom components
import { WorkTimesContent } from './WorkTimes/WorkTimesContent'
import useGetPcaUserName from '../../../../utils/hooks/useGetPca'

export interface TimesheetServicesProps {
	services: Services[]
	isModal?: boolean
}

function TimesheetServices({ services, isModal = false }: Readonly<TimesheetServicesProps>) {
	dayjs.extend(duration)
	// #region Hooks
	const dispatch = useDispatch<AppDispatch>()
	const isTimeSheetApproved = useIsTimesheetApproved()
	const userNameFromAccount = useGetPcaUserName()
	const extraTimePayment = useSelector((state: TimesheetStoreState) =>
		isTimeSheetApproved
			? state.timesheetPage.approval?.extraTimePayment
			: (userNameFromAccount?.toLowerCase().includes('transol') ?? false) ||
				  (userNameFromAccount?.toLowerCase().includes('eva') ?? false) ||
				  (userNameFromAccount?.toLowerCase().includes('rodest') ?? false) ||
				  (userNameFromAccount?.toLowerCase().includes('frotazul-algarve') ?? false)
				? true
				: state.timesheetPage.extraTimePayment,
	)
	// #endregion Hooks

	// #region Redux state selectors
	const initialStatus = useSelector((state: TimesheetStoreState) =>
		isTimeSheetApproved ? state.timesheetApprovalDetailsAccept.initialStatus : state.timesheetPage.initialStatus,
	)

	const timesheetWorkTimes = useSelector((state: TimesheetStoreState) => isTimeSheetApproved ? state.timesheetApprovalDetailsAccept.workTimes : state.timesheetPage.workTimes)
	

	const timeSheet = useSelector((state: TimesheetStoreState) => state.timesheetPage)

	const timesheetApprovalDetailsAccept = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept,
	)
	// #endregion Redux state selectors

	// #region Local state management
	const [isRelaxed, setIsRelaxed] = useState<boolean>(false)
	const [workTimes, setWorkTimes] = useState<WorkTimePeriod[]>(

		timesheetWorkTimes.length > 0
			? timesheetWorkTimes.map(workTime => ({
					_key: crypto.randomUUID(), // unique per entry so React identifies each row independently of its index
					startTime: dayjs(workTime.startTime),
					isStartTimeValid: dayjs(workTime.startTime).isValid(),
					startDate: dayjs(workTime.startTime),
					isStartDateValid: dayjs(workTime.startTime).isValid(),
					endTime: dayjs(workTime.endTime),
					isEndTimeValid: dayjs(workTime.endTime).isValid(),
					endDate: dayjs(workTime.endTime),
					isEndDateValid: dayjs(workTime.endTime).isValid(),
				}))
			: [],
	)

	// Extra Time Payment state from Redux store to local state
	const [paysExtraTime, setPaysExtraTime] = useState<boolean>(extraTimePayment ?? false)

	// Filter out reserve services as they are not relevant for the calculations and display in this component
	const filteredTimesheets = (timeSheet.services || []).filter(service => !service.service.isReserve)
	// #endregion Local state management

	// #region Calculations
	/**
	 * Calculates all work-time totals derived from periods:
	 * - Total worked time (HH:mm)
	 * - Total break time between periods (minutes)
	 * - Total extra time above the daily threshold (HH:mm)
	 */
	const { totalIntervalWorkTimes } = useMemo(() => {
		const periods = getWorkTimePeriods(workTimes)

		// Sum the gap (in minutes) between each valid consecutive work period.
		const totalBreakMinutes = periods.reduce((total, period, index) => {
			const nextPeriod = periods[index + 1]
			if (index === periods.length - 1) return total
			if (!period.end?.isValid() || !nextPeriod?.start?.isValid()) return total
			return total + nextPeriod.start.diff(period.end, 'minute')
		}, 0)

		return {
			totalIntervalWorkTimes: totalBreakMinutes,
		}
	}, [workTimes])

	// #endregion Calculations

	// #region Handlers
	/**
	 * Handles changes to the "Pays Extra Time" checkbox
	 * @param event - Event from the checkbox change
	 */
	const handleExtraTimePayChange = (event: SyntheticEvent<Element, Event>) => {
		const checked = (event.target as HTMLInputElement).checked
		setPaysExtraTime(checked)
		dispatch(
			acceptApproval({
				...timesheetApprovalDetailsAccept,
				extraTimePayment: checked,
			}),
		)
		dispatch(handleDetails({ extraTimePayment: checked }))
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	/**
	 * Adds a new work period to the list of work times
	 * The new period is added after the last filled period, or at the end if no periods are filled.
	 * If there is a previous period, the new period's start date is pre-filled with the previous period's end date for user convenience.
	 * After adding the new period, the function focuses on the start time input of the new period to streamline data entry for the user.
	 * @dependencies workTimes - Current work times state
	 * @dependencies timeSheet.timeSheetDate - Date of the timesheet, used for pre-filling the start date if no periods exist
	 * @description This function handles the addition of a new work period. It determines where to add the new period based on the last filled period and pre-fills the start date if possible. It then updates the local state with the new list of work times and focuses on the start time input of the newly added period to enhance user experience.
	 */
	const handleAddNewPeriod = () => {
		const updatedWorkTimes = [...workTimes]
		// Find the last filled period
		const lastFilledIndex = [...updatedWorkTimes]
			.reverse()
			.findIndex(
				workTime =>
					workTime.startTime?.isValid() &&
					workTime.endTime?.isValid() &&
					workTime.startDate?.isValid() &&
					workTime.endDate?.isValid(),
			)
		// Convert reverse index to original index
		const actualLastFilledIndex = lastFilledIndex === -1 ? -1 : updatedWorkTimes.length - 1 - lastFilledIndex

		// If no filled period is found, add at the end
		const nextIndex = actualLastFilledIndex === -1 ? updatedWorkTimes.length : actualLastFilledIndex + 1

		// If there's a previous period, use its end time as reference
		let newStartDate = dayjs()
		let newEndDate = dayjs()
		let newEndTime = dayjs()

		if (nextIndex > 0) {
			const previousPeriod = updatedWorkTimes[nextIndex - 1]
			if (previousPeriod.endDate?.isValid()) {
				newStartDate = dayjs(previousPeriod.endDate)
				newEndDate = dayjs(previousPeriod.endDate)
				newEndTime = dayjs(previousPeriod.endTime)
			}
		} else if (updatedWorkTimes.length === 0) {
			// If no periods exist, use the initial registration date
			const sheetDate = timeSheet.timeSheetDate
			if (sheetDate) {
				const parsedDate = dayjs(sheetDate)
				if (parsedDate.isValid()) {
					newStartDate = parsedDate
					newEndDate = parsedDate
				}
			}
		}

		updatedWorkTimes[nextIndex] = {
			_key: crypto.randomUUID(),
			// startTime é null por deliberação: o utilizador deve inserir a hora manualmente.
			// Pré-preencher com dayjs() causava dispatch prematuro ao Redux (startTime era dayjs válido)
			// e mostrava a hora atual sem o utilizador ter confirmado nada.
			startTime: null,
			isStartTimeValid: false,
			// startDate fica pré-preenchida com a data do período anterior (ou hoje), conforme pedido.
			startDate: newStartDate?.isValid() ? newStartDate : null,
			isStartDateValid: newStartDate?.isValid() ?? false,
			endTime: newEndTime?.isValid() ? newEndTime : null,
			isEndTimeValid: false,
			endDate: newEndDate?.isValid() ? newEndDate : null,
			isEndDateValid: newEndDate?.isValid() ?? false,
		}

		updatedWorkTimes[nextIndex - 1] = {
			...updatedWorkTimes[nextIndex - 1],
			endTime: null,
		}

		setWorkTimes(updatedWorkTimes)

		// Focus on time field after a small delay
		setTimeout(() => {
			const timeInput = document.querySelector(`input[name="startTime-${nextIndex}"]`) as HTMLInputElement
			if (timeInput) {
				timeInput.focus()
			}
		}, 100)
	}

	/**
	 * Helper function to handle date/time changes and update the store
	 * @param type - Type of change ('start' or 'end')
	 * @param fieldType - Type of field ('time' or 'date')
	 * @param newValue - New date/time value
	 * @param index - Index of the work period
	 * @param workTimes - Current work times array
	 * @param timesheetApprovalDetailsAccept - Current store state
	 * @param dispatch - Redux dispatch function
	 */
	const handleDateTimeChange = (
		type: 'start' | 'end',
		fieldType: 'time' | 'date',
		newValue: dayjs.Dayjs | null,
		index: number,
		workTimes: WorkTimePeriod[],
		timesheetApprovalDetailsAccept: ApprovalRequest,
		dispatch: AppDispatch,
	) => {
		const updatedWorkTimes = [...workTimes]
		const timeField = type === 'start' ? 'startTime' : 'endTime'
		const dateField = type === 'start' ? 'startDate' : 'endDate'

		updatedWorkTimes[index] = {
			...updatedWorkTimes[index],
			[fieldType === 'time' ? timeField : dateField]: newValue,
		}

		if (updatedWorkTimes[index][timeField]?.isValid() && updatedWorkTimes[index][dateField]?.isValid()) {
			const date = updatedWorkTimes[index][dateField]
			const time = updatedWorkTimes[index][timeField]

			if (date && time) {
				const dateTime = date.hour(time.hour()).minute(time.minute())

				// Validate that the combined datetime is valid and within SQL Server range
				if (!dateTime.isValid() || dateTime.year() < 1753 || dateTime.year() > 9999) {
					devWarn('Invalid datetime detected, skipping dispatch:', dateTime.format())
					return updatedWorkTimes
				}

				let indexExists = false

				if (isTimeSheetApproved) {
					indexExists = timesheetApprovalDetailsAccept.workTimes?.some((_: WorktimesRequest, i: number) => i === index)
				} else {
					indexExists = timeSheet.workTimes?.some((_: WorktimesRequest, i: number) => i === index)
				}
				const storeField = type === 'start' ? 'startTime' : 'endTime'

				if (indexExists) {
					if (isTimeSheetApproved) {
						dispatch(
							acceptApproval({
								...timesheetApprovalDetailsAccept,
								workTimes: timesheetApprovalDetailsAccept.workTimes.map((wt, i: number) =>
									i === index
										? {
												...wt,
												[storeField]: dateTime.format('YYYY-MM-DDTHH:mm:ss'),
											}
										: wt,
								),
							}),
						)
					} else {
						dispatch(
							handleTimesheetWorkTimesDetails(
								timeSheet.workTimes.map((wt, i: number) =>
									i === index
										? {
												...wt,
												[storeField]: dateTime.format('YYYY-MM-DDTHH:mm:ss'),
											}
										: wt,
								),
							),
						)
					}
				} else {
					// Carry over a pre-filled endTime from local state (set by handleAddNewPeriod)
					// so that saving works even when the user does not interact with the pre-filled field.
					const localEndTime = updatedWorkTimes[index].endTime
					const localEndDate = updatedWorkTimes[index].endDate
					let prefilledEndDateTime: string | null = null
					if (localEndTime?.isValid() && localEndDate?.isValid()) {
						const combined = localEndDate
							.hour(localEndTime.hour())
							.minute(localEndTime.minute())
							.second(localEndTime.second())
						if (combined.isValid() && combined.year() >= 1753 && combined.year() <= 9999) {
							prefilledEndDateTime = combined.format('YYYY-MM-DDTHH:mm:ss')
						}
					}

					if (isTimeSheetApproved) {
						const updatedWorkTimesStore = [...timesheetApprovalDetailsAccept.workTimes]
						const newWorkTime: WorktimesRequest = {
							startTime:
								type === 'start'
									? dateTime.format('YYYY-MM-DDTHH:mm:ss')
									: timesheetApprovalDetailsAccept.workTimes?.[timesheetApprovalDetailsAccept.workTimes.length - 1]
											?.endTime ??
										initialStatus?.regDate ??
										'',
							endTime: type === 'end'
								? dateTime.format('YYYY-MM-DDTHH:mm:ss')
								: (prefilledEndDateTime ?? undefined),
							isClean:
								timesheetApprovalDetailsAccept.workTimes?.[timesheetApprovalDetailsAccept.workTimes.length - 1]
									?.isClean ?? false,
						}

						updatedWorkTimesStore.push(newWorkTime)

						dispatch(
							acceptApproval({
								...timesheetApprovalDetailsAccept,
								workTimes: updatedWorkTimesStore,
							}),
						)
					} else {
						const updatedWorkTimesStore = [...timeSheet.workTimes]

						const newWorkTime: WorkTime = {
							timeSheetApprovalWorkTimesId: 0,
							startTime:
								type === 'start'
									? dateTime.format('YYYY-MM-DDTHH:mm:ss')
									: timeSheet.workTimes?.[timeSheet.workTimes.length - 1]?.endTime ?? initialStatus?.regDate ?? '',
							endTime: type === 'end'
								? dateTime.format('YYYY-MM-DDTHH:mm:ss')
								: (prefilledEndDateTime ?? ''),
							isClean: timeSheet.workTimes?.[timeSheet.workTimes.length - 1]?.isClean ?? false,
						}

						updatedWorkTimesStore.push(newWorkTime)

						dispatch(handleTimesheetWorkTimesDetails(updatedWorkTimesStore))
					}
				}
				dispatch(hasChanges({ hasChangesFromReviewer: true }))
			}
		}

		return updatedWorkTimes
	}

	/**
	 * Updates start time for a specific work period
	 * @param newValue - New time value
	 * @param index - Index of the work period
	 */
	const handleStartTimeChange = (newValue: dayjs.Dayjs | null, index: number) => {
		setWorkTimes(
			handleDateTimeChange('start', 'time', newValue, index, workTimes, timesheetApprovalDetailsAccept, dispatch),
		)
	}

	/**
	 * Updates start date for a specific work period
	 * @param newValue - New date value
	 * @param index - Index of the work period
	 */
	const handleStartDateChange = (newValue: dayjs.Dayjs | null, index: number) => {
		setWorkTimes(
			handleDateTimeChange('start', 'date', newValue, index, workTimes, timesheetApprovalDetailsAccept, dispatch),
		)
	}

	/**
	 * Updates end time for a specific work period
	 * @param newValue - New time value
	 * @param index - Index of the work period
	 */
	const handleEndTimeChange = (newValue: dayjs.Dayjs | null, index: number) => {
		setWorkTimes(
			handleDateTimeChange('end', 'time', newValue, index, workTimes, timesheetApprovalDetailsAccept, dispatch),
		)
	}

	/**
	 * Updates end date for a specific work period
	 * @param newValue - New date value
	 * @param index - Index of the work period
	 */
	const handleEndDateChange = (newValue: dayjs.Dayjs | null, index: number) => {
		setWorkTimes(
			handleDateTimeChange('end', 'date', newValue, index, workTimes, timesheetApprovalDetailsAccept, dispatch),
		)
	}

	/**
	 * Removes a work period based on its index
	 * If the removed period has a valid end time and it's not the first period, it updates the previous period's end time to maintain continuity
	 * @param index - Index of the work period to be removed
	 * @dependencies workTimes - Current work times state
	 * @dependencies timesheetApprovalDetailsAccept - Current acceptance state from the Redux store
	 * @dependencies timeSheet - Current timesheet details from the Redux store
	 * @dependencies dispatch - Redux dispatch function
	 */
	const handleRemovePeriod = (index: number) => {
		const removedWorkTime = workTimes[index]
		const removedEndTime = removedWorkTime?.endTime
		const hasValidRemovedEndTime = removedEndTime?.isValid() ?? false

		const updatedWorkTimes = workTimes.filter((_, i) => i !== index)

		if (index > 0 && hasValidRemovedEndTime) {
			updatedWorkTimes[index - 1] = {
				...updatedWorkTimes[index - 1],
				endTime: removedEndTime,
			}
		}

		setWorkTimes(updatedWorkTimes)
		if (isTimeSheetApproved) {
			const updatedApprovedWorkTimes = timesheetApprovalDetailsAccept.workTimes.filter((wt, i) => i !== index)

			if (index > 0 && hasValidRemovedEndTime) {
				updatedApprovedWorkTimes[index - 1] = {
					...updatedApprovedWorkTimes[index - 1],
					endTime: removedEndTime?.format('YYYY-MM-DDTHH:mm:ss'),
				}
			}

			dispatch(
				acceptApproval({
					...timesheetApprovalDetailsAccept,
					workTimes: updatedApprovedWorkTimes,
				}),
			)
		} else {
			const updatedTimesheetWorkTimes = timeSheet.workTimes.filter((wt, i) => i !== index)

			if (index > 0 && hasValidRemovedEndTime) {
				updatedTimesheetWorkTimes[index - 1] = {
					...updatedTimesheetWorkTimes[index - 1],
					endTime: removedEndTime?.format('YYYY-MM-DDTHH:mm:ss') ?? '',
				}
			}

			dispatch(handleTimesheetWorkTimesDetails(updatedTimesheetWorkTimes))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	/**
	 * Handles changes to the "Clean" status checkbox
	 * Updates both local state and Redux store with the new clean status value
	 * This function is triggered when the user toggles the "Clean" checkbox. It updates the clean status in both the acceptance state (if the timesheet is approved) and the details state (if not approved) in the Redux store. It also dispatches an action to indicate that changes have been made from the reviewer, which can be used to enable save buttons or trigger other UI updates.
	 * @dependencies isTimeSheetApproved - Indicates if the timesheet is approved
	 * @dependencies timesheetApprovalDetailsAccept - Current acceptance state from the Redux store
	 * @dependencies timeSheet - Current timesheet details from the Redux store
	 * @dependencies dispatch - Redux dispatch function
	 * @description This function handles the change event for the "Clean" status checkbox. It updates the clean status in the Redux store based on whether the timesheet is approved or not. It ensures that both the acceptance state and details state are updated accordingly, and flags that changes have been made from the reviewer to trigger any necessary UI updates.
	 */
	// KMS inputs were previously local-only (WorkTimesContent state); this handler persists them to
	// Redux so the value is included in the payload when the timesheet is saved or approved.
	const handleStartKmsChange = (value: number) => {
		dispatch(handleTimesheetInitialStatusDetails({ kmsIni: value }))
		if (isTimeSheetApproved) {
			dispatch(
				acceptApproval({
					...timesheetApprovalDetailsAccept,
					initialStatus: {
						...timesheetApprovalDetailsAccept.initialStatus,
						kmsIni: value,
					},
				}),
			)
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	const handleCleanStatusChange = (value: boolean) => {
		dispatch(
			acceptApproval({
				...timesheetApprovalDetailsAccept,
				initialStatus: {
					...timesheetApprovalDetailsAccept.initialStatus,
					isClean: value,
				},
			}),
		)

		dispatch(
			handleTimesheetInitialStatusDetails({
				isClean: value,
			}),
		)
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	// #endregion Handlers

	// #region Lifecycle effects

	/**
	 * Effect to determine if the timesheet should pay extra time based on the driver's company or account username
	 * This effect runs when the component mounts or when the timesheet ID changes.
	 * It checks if the timesheet is approved and if the driver's company name or account username includes "transol" or "eva".
	 * Based on these conditions, it updates the local state and dispatches an action to update the Redux store with the extra time payment status.
	 * @dependencies timeSheet.timeSheetId - ID of the current timesheet
	 * @description This effect checks if the timesheet is approved and if the driver's company name or account username includes "transol" or "eva".
	 * If the timesheet is not approved, it determines the extra time payment status based on the account username and updates both local state and Redux store accordingly.
	 * This ensures that the component reflects the correct extra time payment status based on the driver's information.
	 */
	useEffect(() => {
		if (isTimeSheetApproved) return

		const isExtraTimePayment =
			(userNameFromAccount?.toLowerCase().includes('transol') ?? false) ||
			(userNameFromAccount?.toLowerCase().includes('eva') ?? false) ||
			(userNameFromAccount?.toLowerCase().includes('rodest') ?? false) ||
			(userNameFromAccount?.toLowerCase().includes('frotazul-algarve') ?? false)

		dispatch(handleDetails({ extraTimePayment: isExtraTimePayment }))
		setPaysExtraTime(isExtraTimePayment)
	}, [timeSheet.timeSheetId, userNameFromAccount])

	/**
	 * Effect to set relaxed mode for modals
	 * This effect runs when the component mounts or when the isModal or isRelaxed state changes.
	 * If the component is rendered as a modal and relaxed mode is not already enabled, it enables relaxed mode by updating the local state.
	 * This is useful for adjusting the component's layout and behavior when displayed in a modal context, ensuring a better user experience.
	 * @dependencies isModal - Indicates if the component is rendered as a modal
	 * @dependencies isRelaxed - Indicates if relaxed mode is currently enabled
	 */
	useEffect(() => {
		if (isModal && !isRelaxed) setIsRelaxed(true)
	}, [isModal, isRelaxed])

	/**
	 * Effect to update Redux store with work times whenever they change in local state
	 * This effect runs whenever the local workTimes state changes.
	 * It checks if there are any work times and dispatches an action to update the Redux store with the latest work times.
	 * This ensures that any changes made to the work times in the component are reflected in the Redux store, keeping the global state in sync with the component's local state.
	 * @dependencies workTimes - Local state of work times in the component
	 * @description This effect listens for changes in the local workTimes state and updates the Redux store with the latest work times whenever they change.
	 * It ensures that the global state remains consistent with the component's local state, allowing for accurate data management and display across the application.
	 */
	useEffect(() => {
		workTimes.length > 0 && dispatch(handleTimesheetWorkTimesDetails(timesheetWorkTimes)) // Ensure store is updated with the latest work times
	}, [workTimes])

	/**
	 * Effect to update Redux store with extra time payment status whenever it changes in local state
	 * This effect runs whenever the local paysExtraTime state changes.
	 * It dispatches actions to update both the approval details and timesheet details in the Redux store with the latest extra time payment status.
	 */
	useEffect(() => {
		dispatch(handleTimesheetApprovalDetails({ extraTimePayment: paysExtraTime })) // Update approval details with the latest extra time payment status
		dispatch(handleDetails({ extraTimePayment: paysExtraTime })) // Update timesheet details with the latest extra time payment status
	}, [paysExtraTime])

	// #endregion Lifecycle effects

	return (
		<WorkTimesContent
			workTimes={workTimes}
			initialStatus={initialStatus}
			paysExtraTime={paysExtraTime}
			services={services}
			timeSheet={timeSheet}
			filteredTimesheets={filteredTimesheets}
			totalIntervalWorkTimes={totalIntervalWorkTimes}
			handleStartTimeChange={handleStartTimeChange}
			handleStartDateChange={handleStartDateChange}
			handleEndTimeChange={handleEndTimeChange}
			handleEndDateChange={handleEndDateChange}
			handleRemovePeriod={handleRemovePeriod}
			handleAddNewPeriod={handleAddNewPeriod}
			handleExtraTimePayChange={handleExtraTimePayChange}
			handleCleanStatusChange={handleCleanStatusChange}
			handleStartKmsChange={handleStartKmsChange}
		/>
	)
}

export default TimesheetServices
